import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { API } from "../App";
import { PhoneVerification } from "../components/PhoneVerification";
import { Users, MapPin, Loader2, Upload, CheckCircle2 } from "lucide-react";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/203f7def-ad2c-4f8f-807f-2bc10f2f16d6/images/a913d16ba95302b639f95999ec0d2a7b5d654d2a1647e0be42e9b257d09c7d8a.png";

// Validação de CPF/CNPJ pelos dígitos verificadores (anti-fake)
const isValidCpfCnpj = (digits) => {
  if (digits.length === 11) {
    if (/^(\d)\1{10}$/.test(digits)) return false;
    for (const i of [9, 10]) {
      let s = 0;
      for (let j = 0; j < i; j++) s += parseInt(digits[j]) * (i + 1 - j);
      let d = (s * 10) % 11;
      if (d === 10) d = 0;
      if (d !== parseInt(digits[i])) return false;
    }
    return true;
  }
  if (digits.length === 14) {
    if (/^(\d)\1{13}$/.test(digits)) return false;
    const calc = (base, weights) => {
      const s = base.split("").reduce((acc, d, i) => acc + parseInt(d) * weights[i], 0);
      const r = s % 11;
      return r < 2 ? 0 : 11 - r;
    };
    const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const w2 = [6, ...w1];
    return calc(digits.slice(0, 12), w1) === parseInt(digits[12]) && calc(digits.slice(0, 13), w2) === parseInt(digits[13]);
  }
  return false;
};

const RegisterPage = ({ setToken, setUser }) => {
  const { referralCode } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [cnhUploading, setCnhUploading] = useState(false);
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [phoneCfg, setPhoneCfg] = useState({ enabled: false });
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    user_type: "",  // Vazio força usuário a escolher explicitamente
    referrer_code: referralCode || "",
    phone: "",
    cpf_cnpj: "",
    pix_key: "",
    pix_key_type: "cpf",
    address_cep: "",
    address_street: "",
    address_number: "",
    address_complement: "",
    address_neighborhood: "",
    address_city: "",
    address_state: "",
    vehicle_type: "moto",
    cnh_number: "",
    cnh_category: "A",
    vehicle_plate: "",
    cnh_photo_url: "",
    profile_photo_url: "",
    accept_terms: false,
    accept_privacy: false,
    terms_version: "1.0",
  });

  useEffect(() => {
    if (referralCode) {
      fetchReferrerInfo();
    }
  }, [referralCode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/auth/phone/config`);
        if (r.ok) setPhoneCfg(await r.json());
      } catch { /* verificação indisponível — segue fluxo normal */ }
    })();
  }, []);

  const fetchReferrerInfo = async () => {
    try {
      const response = await fetch(`${API}/auth/referral-info/${referralCode}`);
      if (response.ok) {
        const data = await response.json();
        setReferrerInfo(data);
      }
    } catch (error) {
      console.error("Error fetching referrer:", error);
    }
  };

  // ViaCEP autofill
  const handleCepBlur = async () => {
    const cep = (formData.address_cep || "").replace(/\D/g, "");
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await r.json();
      if (data.erro) {
        toast.error("CEP não encontrado");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        address_street: data.logradouro || prev.address_street,
        address_neighborhood: data.bairro || prev.address_neighborhood,
        address_city: data.localidade || prev.address_city,
        address_state: data.uf || prev.address_state,
      }));
      toast.success("Endereço preenchido automaticamente");
    } catch (e) {
      toast.error("Erro ao buscar CEP");
    } finally {
      setCepLoading(false);
    }
  };

  // CNH photo upload → base64 (small image; for production use object storage)
  const handleCnhUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Imagem muito grande (máx. 3MB)");
      return;
    }
    setCnhUploading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({ ...prev, cnh_photo_url: reader.result }));
      setCnhUploading(false);
      toast.success("Foto da CNH carregada");
    };
    reader.onerror = () => {
      setCnhUploading(false);
      toast.error("Erro ao carregar foto");
    };
    reader.readAsDataURL(file);
  };

  // Profile photo upload — compressed to ~512px JPEG to keep payload small
  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagem muito grande (máx. 5MB)");
      return;
    }
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const img = new Image();
          img.onload = () => {
            const max = 512;
            const ratio = Math.min(1, max / Math.max(img.width, img.height));
            const w = Math.round(img.width * ratio);
            const h = Math.round(img.height * ratio);
            const canvas = document.createElement("canvas");
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL("image/jpeg", 0.82));
          };
          img.onerror = reject;
          img.src = ev.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      setFormData((prev) => ({ ...prev, profile_photo_url: dataUrl }));
      toast.success("Foto de perfil carregada");
    } catch (err) {
      toast.error("Erro ao processar foto");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Frontend guards
    if (!formData.accept_terms || !formData.accept_privacy) {
      toast.error("Você deve aceitar os Termos de Uso e a Política de Privacidade");
      return;
    }
    const nameParts = formData.name.trim().split(/\s+/).filter((p) => p.length >= 2);
    if (nameParts.length < 2) {
      toast.error("Informe seu nome completo (nome e sobrenome), igual ao CPF");
      return;
    }
    const doc = (formData.cpf_cnpj || "").replace(/\D/g, "");
    if (!isValidCpfCnpj(doc)) {
      toast.error(doc.length === 14 ? "CNPJ inválido — confira os números" : "CPF inválido — confira os números");
      return;
    }
    if (!formData.pix_key || !formData.pix_key_type) {
      toast.error("Informe a chave PIX");
      return;
    }
    if (!formData.user_type) {
      toast.error("⚠️ Escolha o tipo de conta — Cliente, Lojista ou Entregador");
      // Scroll para o seletor pra usuário ver
      try { document.getElementById("user-type-cards")?.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
      return;
    }
    if (phoneCfg.enabled && !phoneVerified) {
      toast.error("Confirme seu número de WhatsApp antes de criar a conta");
      try { document.getElementById("phone")?.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
      return;
    }
    if (formData.user_type === "driver" && !formData.profile_photo_url) {
      toast.error("Envie sua foto de perfil");
      return;
    }
    if (formData.user_type === "driver" && (formData.vehicle_type === "moto" || formData.vehicle_type === "carro") && !formData.cnh_photo_url) {
      toast.error("Envie a foto da CNH");
      return;
    }
    if (formData.user_type === "driver" && formData.vehicle_type === "carro" && !["B", "AB", "C", "AC", "D", "AD", "E", "AE"].includes(formData.cnh_category)) {
      toast.error("Para entregar de carro, a CNH precisa ter categoria B ou superior");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("token", data.token);
        try { localStorage.setItem("user", JSON.stringify(data.user)); } catch { /* quota */ }
        setToken(data.token);
        setUser(data.user);
        try { window.gofoodTrack?.("custom:register_success", { user_type: data.user?.user_type }); } catch {}
        toast.success("Conta criada com sucesso!");
        const redirect = searchParams.get("redirect");
        navigate(redirect && redirect.startsWith("/") ? redirect : "/dashboard");
      } else {
        toast.error(data.detail || "Erro ao criar conta");
      }
    } catch (error) {
      toast.error("Erro ao conectar com o servidor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 flex items-center justify-center p-6">
      <div className="max-w-xl w-full">
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <img loading="lazy" decoding="async" src={LOGO_URL} alt="GoFood" className="w-20 h-20 mx-auto mb-4" />
            </Link>
            <h1 className="font-heading text-3xl font-bold mb-2" data-testid="register-title">Criar Conta</h1>
            <p className="text-gray-600 font-body">Junte-se ao GoFood</p>
          </div>

          {referrerInfo && (
            <div className="bg-gradient-to-r from-primary/10 to-orange-100 border-2 border-primary/20 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-primary" />
                <div>
                  <div className="text-sm text-gray-600">Você foi convidado por:</div>
                  <div className="font-bold text-primary">{referrerInfo.name}</div>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic */}
            <div>
              <Label htmlFor="name">Nome Completo (igual ao CPF)</Label>
              <Input id="name" type="text" required value={formData.name}
                placeholder="Nome e sobrenome, como no documento"
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1" data-testid="register-name-input" />
              <p className="text-[11px] text-gray-500 mt-1">Use seu nome completo exatamente como consta no seu CPF.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1" data-testid="register-email-input" />
              </div>
              <div>
                <Label htmlFor="phone">Telefone (WhatsApp)</Label>
                <Input id="phone" type="tel" placeholder="11999999999" value={formData.phone}
                  required={phoneCfg.enabled}
                  onChange={(e) => { setFormData({ ...formData, phone: e.target.value }); if (phoneVerified) setPhoneVerified(false); }}
                  className="mt-1" data-testid="register-phone-input" />
              </div>
            </div>

            {phoneCfg.enabled && (
              <PhoneVerification
                phone={formData.phone}
                verified={phoneVerified}
                onVerified={() => setPhoneVerified(true)}
                onChangeNumber={() => { try { document.getElementById("phone")?.focus(); } catch {} }}
              />
            )}

            <div>
              <Label htmlFor="password">Senha</Label>
              <PasswordInput id="password" required value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                containerClassName="mt-1" data-testid="register-password-input" />
            </div>

            <div>
              <Label htmlFor="cpf_cnpj">CPF ou CNPJ</Label>
              <Input id="cpf_cnpj" type="text" required placeholder="Somente números"
                value={formData.cpf_cnpj}
                onChange={(e) => setFormData({ ...formData, cpf_cnpj: e.target.value })}
                className="mt-1" data-testid="register-cpf-cnpj-input" />
            </div>

            {/* Account Type — CARDS GRANDES OBRIGATÓRIOS */}
            <div id="user-type-cards">
              <Label className="text-base font-bold flex items-center gap-2 mb-3">
                <span className={formData.user_type ? "text-emerald-600" : "text-red-600"}>
                  {formData.user_type ? "✅" : "⚠️"}
                </span>
                Como você vai usar o GoFood? <span className="text-red-600">*obrigatório</span>
              </Label>
              <div className="grid grid-cols-3 gap-2" data-testid="register-user-type-cards">
                {[
                  { value: "user", label: "Cliente", emoji: "🛒", desc: "Fazer pedidos", color: "from-blue-500 to-blue-600" },
                  { value: "merchant", label: "Lojista", emoji: "🏪", desc: "Vender meus produtos", color: "from-orange-500 to-orange-600" },
                  { value: "driver", label: "Entregador", emoji: "🏍️", desc: "Ganhar com entregas", color: "from-emerald-500 to-emerald-600" },
                ].map((opt) => {
                  const active = formData.user_type === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, user_type: opt.value })}
                      data-testid={`register-user-type-${opt.value}`}
                      className={`relative text-center p-3 rounded-xl border-2 transition-all ${
                        active
                          ? `border-transparent bg-gradient-to-br ${opt.color} text-white shadow-lg scale-105`
                          : "border-gray-200 bg-white hover:border-gray-400 text-gray-800"
                      }`}
                    >
                      <div className="text-3xl mb-1">{opt.emoji}</div>
                      <div className="font-extrabold text-sm">{opt.label}</div>
                      <div className={`text-[10px] mt-0.5 ${active ? "text-white/90" : "text-gray-500"}`}>{opt.desc}</div>
                      {active && (
                        <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white text-emerald-600 flex items-center justify-center text-xs font-bold">✓</div>
                      )}
                    </button>
                  );
                })}
              </div>
              {!formData.user_type && (
                <p className="text-xs text-red-600 mt-2 font-bold">👆 Você precisa escolher um tipo de conta antes de continuar</p>
              )}
            </div>

            {/* PIX Key */}
            <div className="p-4 bg-emerald-50 border-2 border-emerald-200 rounded-xl space-y-3" data-testid="pix-section">
              <div className="text-sm font-semibold text-emerald-900">Chave PIX (para receber pagamentos/saques)</div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <Label>Tipo</Label>
                  <Select value={formData.pix_key_type}
                    onValueChange={(value) => setFormData({ ...formData, pix_key_type: value })}>
                    <SelectTrigger className="mt-1" data-testid="register-pix-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cpf">CPF</SelectItem>
                      <SelectItem value="cnpj">CNPJ</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Telefone</SelectItem>
                      <SelectItem value="random">Aleatória</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Chave PIX</Label>
                  <Input type="text" required value={formData.pix_key}
                    onChange={(e) => setFormData({ ...formData, pix_key: e.target.value })}
                    placeholder="Digite sua chave"
                    className="mt-1" data-testid="register-pix-key-input" />
                </div>
              </div>
            </div>

            {/* Address with ViaCEP */}
            <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-xl space-y-3" data-testid="address-section">
              <div className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Endereço
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1 relative">
                  <Label>CEP</Label>
                  <Input type="text" required value={formData.address_cep}
                    onChange={(e) => setFormData({ ...formData, address_cep: e.target.value })}
                    onBlur={handleCepBlur}
                    placeholder="00000-000"
                    className="mt-1" data-testid="register-cep-input" />
                  {cepLoading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-9 text-blue-600" />}
                </div>
                <div className="col-span-2">
                  <Label>Rua</Label>
                  <Input type="text" required value={formData.address_street}
                    onChange={(e) => setFormData({ ...formData, address_street: e.target.value })}
                    className="mt-1" data-testid="register-street-input" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="block truncate">Número</Label>
                  <Input type="text" required value={formData.address_number}
                    onChange={(e) => setFormData({ ...formData, address_number: e.target.value })}
                    className="mt-1" data-testid="register-address-number-input" />
                </div>
                <div className="col-span-2">
                  <Label className="block truncate">Complemento</Label>
                  <Input type="text" value={formData.address_complement}
                    onChange={(e) => setFormData({ ...formData, address_complement: e.target.value })}
                    className="mt-1" data-testid="register-address-complement-input" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Bairro</Label>
                  <Input type="text" required value={formData.address_neighborhood}
                    onChange={(e) => setFormData({ ...formData, address_neighborhood: e.target.value })}
                    className="mt-1" data-testid="register-neighborhood-input" />
                </div>
                <div>
                  <Label>Cidade</Label>
                  <Input type="text" required value={formData.address_city}
                    onChange={(e) => setFormData({ ...formData, address_city: e.target.value })}
                    className="mt-1" data-testid="register-city-input" />
                </div>
                <div>
                  <Label>UF</Label>
                  <Input type="text" required maxLength={2} value={formData.address_state}
                    onChange={(e) => setFormData({ ...formData, address_state: e.target.value.toUpperCase() })}
                    className="mt-1" data-testid="register-state-input" />
                </div>
              </div>
            </div>

            {/* Driver Fields */}
            {formData.user_type === "driver" && (
              <div className="space-y-4 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl" data-testid="driver-fields">
                <div>
                  <Label>Modalidade *</Label>
                  <Select value={formData.vehicle_type}
                    onValueChange={(value) => setFormData({ ...formData, vehicle_type: value, cnh_category: value === "carro" ? "B" : value === "moto" ? "A" : "" })}>
                    <SelectTrigger className="mt-1" data-testid="register-vehicle-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="moto">🏍️ Moto (exige CNH A)</SelectItem>
                      <SelectItem value="carro">🚗 Carro (exige CNH B ou superior)</SelectItem>
                      <SelectItem value="bike">🚲 Bicicleta (sem CNH)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Profile photo — required for all drivers */}
                <div data-testid="register-profile-photo-section">
                  <Label>Foto de perfil *</Label>
                  <label className="mt-1 flex items-center gap-3 border-2 border-dashed border-blue-300 rounded-lg p-3 cursor-pointer hover:bg-blue-50">
                    <input type="file" accept="image/*" className="hidden"
                      onChange={handleProfilePhotoUpload} data-testid="register-profile-photo-input" />
                    {formData.profile_photo_url ? (
                      <>
                        <img loading="lazy" decoding="async" src={formData.profile_photo_url} alt="Pré-visualização" className="w-14 h-14 rounded-full object-cover border-2 border-blue-400" />
                        <span className="text-sm text-blue-700 font-bold">Foto carregada — clique para trocar</span>
                      </>
                    ) : (
                      <>
                        <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center text-blue-500">
                          <Upload className="w-5 h-5" />
                        </div>
                        <span className="text-sm text-blue-700">Selecione uma foto sua (será comprimida automaticamente)</span>
                      </>
                    )}
                  </label>
                </div>

                {(formData.vehicle_type === "moto" || formData.vehicle_type === "carro") && (
                  <>
                    <div>
                      <Label htmlFor="cnh_number">Número da CNH</Label>
                      <Input id="cnh_number" type="text" required value={formData.cnh_number}
                        onChange={(e) => setFormData({ ...formData, cnh_number: e.target.value })}
                        placeholder="11 dígitos" className="mt-1" data-testid="register-cnh-number-input" />
                    </div>
                    <div>
                      <Label>
                        {formData.vehicle_type === "carro"
                          ? "Categoria CNH (B ou superior)"
                          : "Categoria CNH (deve conter A)"}
                      </Label>
                      <Select value={formData.cnh_category}
                        onValueChange={(value) => setFormData({ ...formData, cnh_category: value })}>
                        <SelectTrigger className="mt-1" data-testid="register-cnh-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {formData.vehicle_type === "moto" ? (
                            <>
                              <SelectItem value="A">A (somente moto)</SelectItem>
                              <SelectItem value="AB">AB (moto + carro)</SelectItem>
                              <SelectItem value="AC">AC (moto + caminhão)</SelectItem>
                              <SelectItem value="AD">AD (moto + ônibus)</SelectItem>
                              <SelectItem value="AE">AE (moto + carreta)</SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="B">B (carro)</SelectItem>
                              <SelectItem value="AB">AB (moto + carro)</SelectItem>
                              <SelectItem value="C">C (carro + caminhão pequeno)</SelectItem>
                              <SelectItem value="AC">AC (moto + caminhão)</SelectItem>
                              <SelectItem value="D">D (carro + ônibus)</SelectItem>
                              <SelectItem value="AD">AD (moto + ônibus)</SelectItem>
                              <SelectItem value="E">E (carro + carreta)</SelectItem>
                              <SelectItem value="AE">AE (moto + carreta)</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="vehicle_plate">
                        Placa {formData.vehicle_type === "carro" ? "do Carro" : "da Moto"}
                      </Label>
                      <Input id="vehicle_plate" type="text" required value={formData.vehicle_plate}
                        onChange={(e) => setFormData({ ...formData, vehicle_plate: e.target.value.toUpperCase() })}
                        placeholder="ABC1D23" maxLength={8}
                        className="mt-1" data-testid="register-vehicle-plate-input" />
                    </div>
                    {formData.vehicle_type === "carro" && (
                      <div>
                        <Label htmlFor="vehicle_model">Marca / Modelo do Carro</Label>
                        <Input id="vehicle_model" type="text" required value={formData.vehicle_model || ""}
                          onChange={(e) => setFormData({ ...formData, vehicle_model: e.target.value })}
                          placeholder="Ex.: Fiat Uno 2018"
                          className="mt-1" data-testid="register-vehicle-model-input" />
                      </div>
                    )}
                    <div>
                      <Label>Foto da CNH *</Label>
                      <label className="mt-1 flex items-center gap-3 border-2 border-dashed border-yellow-400 rounded-lg p-3 cursor-pointer hover:bg-yellow-100">
                        <input type="file" accept="image/*" className="hidden"
                          onChange={handleCnhUpload} data-testid="register-cnh-photo-input" />
                        {cnhUploading ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</>
                        ) : formData.cnh_photo_url ? (
                          <><CheckCircle2 className="w-5 h-5 text-green-600" /><span className="text-sm">Foto carregada</span></>
                        ) : (
                          <><Upload className="w-4 h-4" /><span className="text-sm">Clique para enviar (JPG/PNG, máx 3MB)</span></>
                        )}
                      </label>
                    </div>
                    {formData.vehicle_type === "carro" && (
                      <p className="text-xs text-yellow-900 bg-yellow-100 border border-yellow-300 p-2.5 rounded-lg">
                        <strong>⚠️ Documentação do carro:</strong> O cadastro do CRLV (documento do veículo) e foto do carro
                        serão solicitados após login — necessários antes de você poder pegar corridas.
                      </p>
                    )}
                  </>
                )}
                {formData.vehicle_type === "bike" && (
                  <p className="text-sm text-yellow-800 bg-yellow-100 p-3 rounded-lg">
                    Entregadores de bicicleta não precisam de CNH. Basta continuar o cadastro!
                  </p>
                )}
              </div>
            )}

            {/* Referral Code */}
            <div>
              <Label htmlFor="referrer_code">Código de Indicação</Label>
              <Input id="referrer_code" type="text" required value={formData.referrer_code}
                onChange={(e) => setFormData({ ...formData, referrer_code: e.target.value.toUpperCase() })}
                className="mt-1" disabled={!!referralCode}
                data-testid="register-referral-code-input" />
            </div>

            {/* LGPD Consents */}
            <div className="p-4 bg-gray-50 border-2 border-gray-200 rounded-xl space-y-3" data-testid="lgpd-section">
              <div className="text-sm font-semibold text-gray-900">Consentimentos (LGPD)</div>
              <div className="flex items-start gap-2">
                <Checkbox id="accept_terms" checked={formData.accept_terms}
                  onCheckedChange={(v) => setFormData({ ...formData, accept_terms: !!v })}
                  data-testid="register-accept-terms-checkbox" />
                <label htmlFor="accept_terms" className="text-sm text-gray-700 leading-tight cursor-pointer">
                  Li e aceito os <a href="/termos" target="_blank" rel="noreferrer" className="text-primary underline">Termos de Uso</a>
                </label>
              </div>
              <div className="flex items-start gap-2">
                <Checkbox id="accept_privacy" checked={formData.accept_privacy}
                  onCheckedChange={(v) => setFormData({ ...formData, accept_privacy: !!v })}
                  data-testid="register-accept-privacy-checkbox" />
                <label htmlFor="accept_privacy" className="text-sm text-gray-700 leading-tight cursor-pointer">
                  Li e concordo com a <a href="/privacidade" target="_blank" rel="noreferrer" className="text-primary underline">Política de Privacidade</a> (LGPD)
                </label>
              </div>
            </div>

            <Button type="submit"
              className="w-full bg-gradient-to-r from-primary to-orange-500 hover:from-primary/90 hover:to-orange-500/90 text-white font-semibold py-6 rounded-full"
              disabled={loading} data-testid="register-submit-button">
              {loading ? "Criando conta..." : "Criar Conta"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600 font-body text-sm">
              Já tem uma conta?{" "}
              <Link to="/login" className="text-primary font-semibold hover:underline">
                Faça login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
